import React, { useCallback, useEffect } from 'react';
import { TextField, Select, MenuItem, FormControl, InputLabel, Alert, Table, TableBody, TableCell, TableHead, TableRow, Typography, Paper, Grid } from '@mui/material';
import { useDataProvider } from 'react-admin';
import { z } from 'zod';
import { useWizard } from '@/components/wizard/useWizard';
import { WizardLayout } from '@/components/wizard/WizardLayout';
import { OutcomeSummary } from '@/components/wizard/OutcomeSummary';
import { useRuleEvaluation } from '@/components/workflow';

import { stepValidator } from '@/components/wizard/validateWithZod';

const STEPS = [
  { id: 'year', title: 'Reporting Year' },
  { id: 'workforce', title: 'Workforce Snapshot' },
  { id: 'training', title: 'Training Aggregation' },
  { id: 'bbbee', title: 'B-BBEE Spend' },
  { id: 'review', title: 'Review & Submit' },
];

const OCCUPATIONAL_LEVELS = ['Top Management', 'Senior Management', 'Professional', 'Skilled', 'Semi-Skilled', 'Unskilled'];
const RACE_CATEGORIES = ['African', 'Coloured', 'Indian', 'White'];
const GENDER_CATEGORIES = ['Male', 'Female'];
const SETA_NAMES = ['ETDP SETA', 'MICT SETA', 'FoodBev SETA', 'Chieta', 'W&R SETA', 'Seta for Wholesale & Retail', 'Transport SETA', 'Safety & Security SETA'];

interface WorkforceCount {
  level: string;
  race: string;
  gender: string;
  count: number;
}

interface FormValues {
  reportingYear: number;
  setaName: string;
  totalEmployees: number;
  workforce: WorkforceCount[];
  trainingHours: number;
  trainingCost: number;
  spendByRace: { race: string; amount: number }[];
}

const getDefaultYear = () => {
  const now = new Date();
  return now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
};

const createInitialWorkforce = (): WorkforceCount[] => {
  const workforce: WorkforceCount[] = [];
  OCCUPATIONAL_LEVELS.forEach(level => {
    RACE_CATEGORIES.forEach(race => {
      GENDER_CATEGORIES.forEach(gender => {
        workforce.push({ level, race, gender, count: 0 });
      });
    });
  });
  return workforce;
};

const createInitialSpendByRace = () => {
  return RACE_CATEGORIES.map(race => ({ race, amount: 0 }));
};

const INITIAL_VALUES: FormValues = {
  reportingYear: getDefaultYear(),
  setaName: '',
  totalEmployees: 0,
  workforce: createInitialWorkforce(),
  trainingHours: 0,
  trainingCost: 0,
  spendByRace: createInitialSpendByRace(),
};

const stepSchemas = {
  year: z.object({
    reportingYear: z.number({ required_error: 'Reporting year is required' }).min(1, 'Reporting year is required'),
    setaName: z.string().min(1, 'SETA name is required'),
  }),
  workforce: z.object({
    totalEmployees: z.number(),
    workforce: z.array(z.object({ level: z.string(), race: z.string(), gender: z.string(), count: z.number() })),
  }),
  training: z.object({
    trainingHours: z.number().positive('Training hours must be greater than 0'),
    trainingCost: z.number().positive('Training cost must be greater than 0'),
  }),
};

function validateStep(stepId: string, values: FormValues) {
  const schema = stepSchemas[stepId as keyof typeof stepSchemas];
  if (!schema) return { isValid: true, errors: {} };

  const baseResult = stepValidator(schema)(values as unknown as Record<string, unknown>);
  if (!baseResult.isValid) return baseResult;

  if (stepId === 'workforce') {
    const totalCount = values.workforce.reduce((sum, w) => sum + w.count, 0);
    if (totalCount === 0) {
      return { isValid: false, errors: { workforce: 'At least one employee must be counted' } };
    }
    if (values.totalEmployees > 0 && totalCount !== values.totalEmployees) {
      return { isValid: false, errors: { totalEmployees: `Total workforce (${totalCount}) must equal total employees (${values.totalEmployees})` } };
    }
  }

  return { isValid: true, errors: {} };
}

export const WspAtrWizard = () => {
  const dataProvider = useDataProvider();

  const {
    currentStep,
    currentStepIndex,
    totalSteps,
    isFirstStep,
    isLastStep,
    isSaving,
    isCompleted,
    progress,
    values,
    errors,
    stepFeedback,
    createdId,
    nextStep,
    prevStep,
    updateValues,
    validateStep: validateStepFn,
    complete,
    reset,
  } = useWizard({
    steps: STEPS,
    initialValues: INITIAL_VALUES as unknown as Record<string, unknown>,
    dataProvider,
    resource: 'wspAtrReports',
  });

  const { evaluate: evaluateRule, isEvaluating: isEvaluatingRule, result: ruleResult } = useRuleEvaluation({
    ruleId: 'wsatr-guards',
    context: {
      entityId: createdId || '',
      entityType: 'process',
      currentState: currentStep?.id || 'initiated',
      event: 'SUBMIT',
      reportingYear: (values as unknown as FormValues).reportingYear,
      setaName: (values as unknown as FormValues).setaName,
    },
  });

  useEffect(() => {
    if (currentStep?.id === 'review') {
      evaluateRule();
    }
  }, [currentStep?.id, evaluateRule]);

  const [workforce, setWorkforce] = React.useState<WorkforceCount[]>(INITIAL_VALUES.workforce);

  const [spendByRace, setSpendByRace] = React.useState(INITIAL_VALUES.spendByRace);

  const getTotalWorkforce = () => workforce.reduce((sum, w) => sum + w.count, 0);

  const updateWorkforce = (index: number, count: number) => {
    const newWorkforce = [...workforce];
    newWorkforce[index].count = count;
    setWorkforce(newWorkforce);
    updateValues({ workforce: newWorkforce });
  };

  const updateSpendByRaceFn = (race: string, amount: number) => {
    const newSpend = spendByRace.map(s => s.race === race ? { ...s, amount } : s);
    setSpendByRace(newSpend);
    updateValues({ spendByRace: newSpend });
  };

  const handleNext = () => {
    const result = validateStep(currentStep.id, { ...values, workforce, spendByRace } as unknown as FormValues);
    if (!result.isValid) {
      validateStepFn(() => result);
      return;
    }
    if (isLastStep) {
      complete();
    } else {
      nextStep();
    }
  };

  const handleSaveDraft = async () => {
    try {
      await dataProvider.create('wspAtrReports', { data: { ...values, workforce, spendByRace, _draft: true } as unknown as FormValues });
    } catch {
      // silently fail for draft
    }
  };

  const handleReset = () => {
    setWorkforce(INITIAL_VALUES.workforce);
    setSpendByRace(INITIAL_VALUES.spendByRace);
    reset();
  };

  const handleComplete = () => {
    complete();
  };

  const handleBack = () => {
    prevStep();
  };

  const renderStepContent = () => {
    if (isCompleted) {
      return (
        <OutcomeSummary
          title="WSP/ATR Report Prepared"
          processId={createdId || undefined}
          fields={[
            { label: 'Reporting Year', value: values.reportingYear as number },
            { label: 'SETA', value: values.setaName as string },
            { label: 'Total Employees', value: getTotalWorkforce() },
            { label: 'Training Hours', value: values.trainingHours as number },
            { label: 'Training Cost', value: `R ${(values.trainingCost as number).toLocaleString()}` },
          ]}
          footerActions={[
            { label: 'View WSP/ATR Reports', to: '/wsatr' },
            { label: 'Start Another', onClick: handleReset },
          ]}
        />
      );
    }

    return (
      <>
        {stepFeedback && <Alert severity={stepFeedback.type}>{stepFeedback.messages.join(', ')}</Alert>}
      </>
    );
  };

  return (
    <WizardLayout
      steps={STEPS}
      currentStepIndex={currentStepIndex}
      progress={progress}
      isSaving={isSaving}
      isCompleted={isCompleted}
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onBack={handleBack}
      onNext={handleNext}
      onSaveDraft={handleSaveDraft}
      onComplete={handleComplete}
      onReset={handleReset}
    >
      {renderStepContent()}

      {currentStep && (
        <Typography variant="subtitle1" sx={{ mb: 0.5, color: 'text.secondary' }}>
          Step {currentStepIndex + 1} of {totalSteps}
        </Typography>
      )}
      {currentStep && (
        <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
          {currentStep.title}
        </Typography>
      )}
      {currentStep && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {getStepDescription(currentStep.id)}
        </Typography>
      )}

      {currentStep && renderFormFields(currentStep.id, values as unknown as FormValues, errors, workforce, spendByRace, updateValues, updateWorkforce, updateSpendByRaceFn, getTotalWorkforce, ruleResult)}
    </WizardLayout>
  );
};

function getStepDescription(stepId: string): string {
  const descriptions: Record<string, string> = {
    year: 'Select the reporting year and SETA for this WSP/ATR submission',
    workforce: 'Enter employee headcount by occupational level, race, and gender',
    training: 'Aggregate total training hours and costs for the reporting period',
    bbbee: 'Categorize training expenditure by race group for B-BBEE compliance',
    review: 'Review all information before final submission',
  };
  return descriptions[stepId] || '';
}

function renderFormFields(
  stepId: string,
  values: FormValues,
  errors: Record<string, string>,
  workforce: WorkforceCount[],
  spendByRace: { race: string; amount: number }[],
  updateValues: (values: Partial<FormValues>) => void,
  updateWorkforce: (index: number, count: number) => void,
  updateSpendByRaceFn: (race: string, amount: number) => void,
  getTotalWorkforce: () => number,
  ruleResult: { allowed: boolean; reason?: string } | null,
): React.ReactNode {
  switch (stepId) {
    case 'year':
      return (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Reporting Year"
              type="number"
              value={values.reportingYear}
              onChange={e => updateValues({ reportingYear: parseInt(e.target.value) })}
              fullWidth
              error={!!errors.reportingYear}
              helperText={errors.reportingYear || 'Prior calendar year default'}
              InputProps={{ inputProps: { min: 2000, max: 2099 } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth error={!!errors.setaName}>
              <InputLabel>SETA Name</InputLabel>
              <Select
                value={values.setaName}
                label="SETA Name"
                onChange={e => updateValues({ setaName: e.target.value })}
              >
                {SETA_NAMES.map(seta => (
                  <MenuItem key={seta} value={seta}>{seta}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Alert severity="info">
              WSP/ATR submissions are mandatory under the Skills Development Act. Deadline is 30 April for the prior year.
            </Alert>
          </Grid>
        </Grid>
      );

    case 'workforce': {
      const totalCount = getTotalWorkforce();
      return (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Total Employees"
              type="number"
              value={values.totalEmployees || ''}
              onChange={e => updateValues({ totalEmployees: parseInt(e.target.value) || 0 })}
              fullWidth
              error={!!errors.totalEmployees}
              helperText={errors.totalEmployees || 'Total headcount at year end'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Calculated Total"
              type="number"
              value={totalCount}
              fullWidth
              disabled
              helperText="Sum of all categories"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Variance"
              value={totalCount - (values.totalEmployees || 0)}
              fullWidth
              disabled
              helperText={totalCount === values.totalEmployees ? 'Balanced' : 'Does not match total'}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Enter employee counts by occupational level, race, and gender
            </Typography>
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Occupational Level</TableCell>
                  {RACE_CATEGORIES.map(race => (
                    <TableCell key={race} sx={{ fontWeight: 700 }} colSpan={2}>{race}</TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell></TableCell>
                  {RACE_CATEGORIES.map(() => (
                    <TableCell sx={{ fontSize: 10 }}>M</TableCell>
                  ))}
                  {RACE_CATEGORIES.map(() => (
                    <TableCell sx={{ fontSize: 10 }}>F</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {OCCUPATIONAL_LEVELS.map(level => (
                  <TableRow key={level}>
                    <TableCell sx={{ fontWeight: 500 }}>{level}</TableCell>
                    {RACE_CATEGORIES.map(race => (
                      <React.Fragment key={race}>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            inputProps={{ min: 0, style: { width: 50 } }}
                            value={workforce.find(w => w.level === level && w.race === race && w.gender === 'Male')?.count || ''}
                            onChange={e => {
                              const idx = workforce.findIndex(w => w.level === level && w.race === race && w.gender === 'Male');
                              if (idx >= 0) updateWorkforce(idx, parseInt(e.target.value) || 0);
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            inputProps={{ min: 0, style: { width: 50 } }}
                            value={workforce.find(w => w.level === level && w.race === race && w.gender === 'Female')?.count || ''}
                            onChange={e => {
                              const idx = workforce.findIndex(w => w.level === level && w.race === race && w.gender === 'Female');
                              if (idx >= 0) updateWorkforce(idx, parseInt(e.target.value) || 0);
                            }}
                          />
                        </TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {errors.workforce && <Alert severity="error">{errors.workforce}</Alert>}
          </Grid>
        </Grid>
      );
    }

    case 'training': {
      return (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Total Training Hours"
              type="number"
              value={values.trainingHours}
              onChange={e => updateValues({ trainingHours: parseInt(e.target.value) || 0 })}
              fullWidth
              error={!!errors.trainingHours}
              helperText={errors.trainingHours || 'Sum of all training interventions'}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Total Training Cost (ZAR)"
              type="number"
              value={values.trainingCost}
              onChange={e => updateValues({ trainingCost: parseFloat(e.target.value) || 0 })}
              fullWidth
              error={!!errors.trainingCost}
              helperText={errors.trainingCost || 'Total spend on training'}
              InputProps={{ startAdornment: 'R ' }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Alert severity="info">
              Training aggregation must reconcile with individual training intervention records. Aggregate hours and costs from all skills programmes, learnerships, and short courses.
            </Alert>
          </Grid>
        </Grid>
      );
    }

    case 'bbbee': {
      const totalSpend = spendByRace.reduce((sum, s) => sum + s.amount, 0);
      return (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Total B-BBEE Training Spend"
              type="number"
              value={totalSpend}
              fullWidth
              disabled
              InputProps={{ startAdornment: 'R ' }}
              helperText="Sum of race categories below"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Training Cost (from previous step)"
              type="number"
              value={values.trainingCost}
              fullWidth
              disabled
              InputProps={{ startAdornment: 'R ' }}
              helperText="Must match total spend above"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Categorize training expenditure by population group for B-BBEE scorecard
            </Typography>
            {spendByRace.map((spend) => (
              <Grid container spacing={2} key={spend.race} sx={{ mb: 1 }}>
                <Grid size={{ xs: 4 }}>
                  <TextField
                    label={spend.race}
                    value={spend.race}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <TextField
                    label="Amount (ZAR)"
                    type="number"
                    value={spend.amount || ''}
                    onChange={e => updateSpendByRaceFn(spend.race, parseFloat(e.target.value) || 0)}
                    fullWidth
                    InputProps={{ startAdornment: 'R ' }}
                  />
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <TextField
                    label="% of Total"
                    value={totalSpend > 0 ? ((spend.amount / totalSpend) * 100).toFixed(1) : '0.0'}
                    fullWidth
                    disabled
                    InputProps={{ endAdornment: '%' }}
                  />
                </Grid>
              </Grid>
            ))}
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Alert severity="success">
              B-BBEE skills development expenditure contributes to enterprise development points. Prioritize African, Coloured, and Female representation in training spend.
            </Alert>
          </Grid>
        </Grid>
      );
    }

    case 'review': {
      return (
        <Grid container spacing={2}>
          {ruleResult && !ruleResult.allowed && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Guard Check</Typography>
                <Typography variant="body2">{ruleResult.reason}</Typography>
              </Alert>
            </Grid>
          )}
          <Grid size={{ xs: 12 }}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Reporting Details</Typography>
              <Grid container spacing={1}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">Reporting Year</Typography>
                  <Typography variant="body2">{values.reportingYear}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">SETA</Typography>
                  <Typography variant="body2">{values.setaName || '-'}</Typography>
                </Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Workforce Summary</Typography>
              <Grid container spacing={1}>
                <Grid size={{ xs: 4 }}>
                  <Typography variant="caption" color="text.secondary">Total Employees</Typography>
                  <Typography variant="body2">{getTotalWorkforce()}</Typography>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Typography variant="caption" color="text.secondary">Training Hours</Typography>
                  <Typography variant="body2">{values.trainingHours}</Typography>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Typography variant="caption" color="text.secondary">Training Cost</Typography>
                  <Typography variant="body2">R {values.trainingCost.toLocaleString()}</Typography>
                </Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>B-BBEE Spend by Race</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Population Group</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Percentage</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {spendByRace.map(spend => {
                    const totalSpendAmt = spendByRace.reduce((sum, s) => sum + s.amount, 0);
                    return (
                      <TableRow key={spend.race}>
                        <TableCell>{spend.race}</TableCell>
                        <TableCell align="right">R {spend.amount.toLocaleString()}</TableCell>
                        <TableCell align="right">{totalSpendAmt > 0 ? ((spend.amount / totalSpendAmt) * 100).toFixed(1) : '0.0'}%</TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      R {spendByRace.reduce((sum, s) => sum + s.amount, 0).toLocaleString()}
                    </TableCell>
                    <TableCell align="right">100%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        </Grid>
      );
    }

    default:
      return null;
  }
}